import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import { updateOpsProgram } from '../../../requests';
import { TOpsProgramDetailEx } from '../../../requests/opsRegistrationsExtra';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  Pill,
} from '../ui';
import { STATUSES } from './shared';

/** Name, status and dates (editable), with the contract's read-only facts. */
const ContractInfoCard: React.FC<{
  programId: string;
  program: TOpsProgramDetailEx;
}> = ({ programId, program }) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({
    name: '',
    status: '',
    startDate: '',
    endDate: '',
  });

  const refreshDetail = () =>
    queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });

  const saveProgram = useMutation({
    mutationFn: () => updateOpsProgram(programId, edit),
    onSuccess: () => {
      showToast('Program saved', 'success');
      setEditing(false);
      refreshDetail();
      queryClient.invalidateQueries({ queryKey: ['opsPrograms'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card
      title="Contract Information"
      right={
        !editing ? (
          <button
            className={btnSecondary}
            onClick={() => {
              setEdit({
                name: program.name ?? '',
                status: program.status ?? '',
                startDate: program.startDate ?? '',
                endDate: program.endDate ?? '',
              });
              setEditing(true);
            }}
          >
            Edit
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <button
              className={btnPrimary}
              onClick={() => saveProgram.mutate()}
              disabled={saveProgram.isPending}
            >
              {saveProgram.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className={btnGhost} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </span>
        )
      }
    >
      <div className="px-5 py-4">
        {editing && (
          <div className="mb-4 flex flex-wrap gap-2">
            <input
              className={`${inputClass} w-full`}
              value={edit.name}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            />
            <select
              className={inputClass}
              value={edit.status}
              onChange={(e) => setEdit({ ...edit, status: e.target.value })}
            >
              {STATUSES.map((st) => (
                <option key={st}>{st}</option>
              ))}
            </select>
            <input
              type="date"
              className={inputClass}
              value={edit.startDate}
              onChange={(e) => setEdit({ ...edit, startDate: e.target.value })}
            />
            <input
              type="date"
              className={inputClass}
              value={edit.endDate}
              onChange={(e) => setEdit({ ...edit, endDate: e.target.value })}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className={labelClass}>Account name</div>
            <div className="text-sm font-medium text-gray-four">
              {program.accountName ?? '—'}
            </div>
          </div>
          <div>
            <div className={labelClass}>Contract number</div>
            <div className="font-mono text-sm text-gray-five">
              {program.number ?? '—'}
            </div>
          </div>
          <div>
            <div className={labelClass}>Contract start → end</div>
            <div className="text-sm text-gray-five">
              {program.startDate} → {program.endDate}
            </div>
          </div>
          <div>
            <div className={labelClass}>Status</div>
            <Pill value={program.status} />
          </div>
          <div>
            <div className={labelClass}>Program members</div>
            <div className="text-sm text-gray-five [font-variant-numeric:tabular-nums]">
              {program.members.length}
              {program.maxMembers != null && ` of ${program.maxMembers}`}
            </div>
          </div>
          <div>
            <div className={labelClass}>Subtype</div>
            <div className="text-sm text-gray-five">
              {program.subtype ?? '—'}
              {program.teamPass && (
                <span className="ml-2">
                  <Pill value="Team Pass" tone="info" />
                </span>
              )}
              {program.anytimeLicense && (
                <span
                  className="ml-2"
                  title="Each class booked against this program draws its course cost once; seats on it are not charged. The class must name the program."
                >
                  <Pill value={program.chargeHint} tone="warn" />
                </span>
              )}
            </div>
          </div>
          <div>
            <div className={labelClass}>Discounts</div>
            <div className="text-sm text-gray-five [font-variant-numeric:tabular-nums]">
              Public {program.publicDiscount ?? 0}% · Private{' '}
              {program.privateDiscount ?? 0}%
              {!program.allowPublicEnrollment && (
                <span className="ml-2 text-xs text-gray-bold">
                  public enrolment off
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ContractInfoCard;
